import { BrowserAgent } from '@newrelic/browser-agent/loaders/browser-agent';
import React from 'react';
import { createRoot } from 'react-dom/client';
import HttpsRedirect from 'react-https-redirect';
import reportWebVitals from './reportWebVitals';
import { IonReactRouter } from '@ionic/react-router';
import { ErrorBoundary } from 'components';
import { ConfigProvider, ThemeConfig } from 'antd';
import { env, func } from 'utils';
import packageJson from '../package.json';

import App from './App';

//
import * as Sentry from '@sentry/react';
import posthog from 'posthog-js';
import { PostHogProvider } from '@posthog/react';

/* Core CSS required for Ionic components to work properly */
import '@ionic/react/css/core.css';

/* Basic CSS for apps built with Ionic */
import '@ionic/react/css/normalize.css';
import '@ionic/react/css/structure.css';
import '@ionic/react/css/typography.css';

/* Optional CSS utils that can be commented out */
import '@ionic/react/css/padding.css';
import '@ionic/react/css/float-elements.css';
import '@ionic/react/css/text-alignment.css';
import '@ionic/react/css/text-transformation.css';
import '@ionic/react/css/flex-utils.css';
import '@ionic/react/css/display.css';

/* Theme variables */
import './theme/variables.css';
import 'bootstrap/dist/css/bootstrap.min.css';

if (!func.isLocal) {
	if (env.NEWRELIC_LICENCE && env.NEWRELIC_APP_ID) {
		const options = {
			info: {
				applicationID: env.NEWRELIC_APP_ID,
				beacon: 'bam.nr-data.net',
				errorBeacon: 'bam.nr-data.net',
				licenseKey: env.NEWRELIC_LICENCE,
				sa: 1,
			},
			init: {
				ajax: {
					deny_list: ['bam.nr-data.net'],
				},
				browser_consent_mode: {
					enabled: false,
				},
				distributed_tracing: {
					enabled: true,
				},
				performance: {
					capture_detail: false,
					capture_marks: false,
					capture_measures: true,
				},
				privacy: {
					cookies_enabled: true,
				},
			},
			loader_config: {
				accountID: env.NEWRELIC_ACC_ID,
				agentID: env.NEWRELIC_APP_ID,
				applicationID: env.NEWRELIC_APP_ID,
				licenseKey: env.NEWRELIC_LICENCE,
				trustKey: env.NEWRELIC_ACC_ID,
			},
		};
		new BrowserAgent(options);
	}

	env.SENTRY_DSN &&
		Sentry.init({
			dsn: env.SENTRY_DSN,
			release: `cr-mobile-${env.NODE_ENV}@${packageJson.version}`,
			integrations: [
				Sentry.browserTracingIntegration(),
				Sentry.replayIntegration(),
			],
			environment: env.NODE_ENV,
			tracesSampleRate: 1.0,
			replaysSessionSampleRate: 0.1,
			replaysOnErrorSampleRate: 1.0,
		});

	env.POSTHOG_TOKEN &&
		posthog.init(env.POSTHOG_TOKEN, {
			api_host: env.POSTHOG_HOST,
			defaults: '2026-05-30',
		});
}

const container = document.getElementById('root');
const root = createRoot(container!);

const antdTheme: ThemeConfig = {
	token: {
		colorPrimary: '#ff1200',
		colorFillSecondary: '#000000',
		borderRadius: 6,
		fontFamily: '"Gotham", sans-serif',
	},
	components: {
		Input: { colorBorder: '#fff' },
	},
};

root.render(
	<React.Fragment>
		<HttpsRedirect>
			<PostHogProvider client={posthog}>
				<div className={`${!func.isMobile ? 'mobile-frame' : ''}`}>
					<IonReactRouter>
						<ErrorBoundary>
							<ConfigProvider theme={antdTheme}>
								<App />
							</ConfigProvider>
						</ErrorBoundary>
					</IonReactRouter>
				</div>
			</PostHogProvider>
		</HttpsRedirect>
	</React.Fragment>
);

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();

// custom service worker
// if (!func.isLocal && 'serviceWorker' in navigator) {
//   navigator.serviceWorker.register(`sw.js?${Date.now()}`)
//     .then(registration => {
//       console.log('Service Worker registered with scope:', registration.scope);
//     })
//     .catch(error => {
//       console.error('Service Worker registration failed:', error);
//     });
// }
